/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useHistory } from "react-router-dom";
import alertify from "alertifyjs";
import {  X } from "lucide-react";
import * as Yup from "yup";
import { Form } from "react-bootstrap";
import FileUpload from "../../components/FileUpload";
import { fetchEmployeesForDropdown } from "../../core/api/employee";
import { fetchPatientsForDropdown } from "../../core/api/patient";
import { createAppointment, getAppointmentById, updateAppointment } from "../../core/api/appointment";


// Constants
const PAYMENT_METHODS = [
  { value: 'Bpomas', label: 'Bpomas' },
  { value: 'Pula', label: 'Pula' },
  { value: 'Cash', label: 'Cash' }
];

const SERVICES = [
  { value: 'Ultrasound', label: 'Ultrasound' },
  { value: 'Fluroscopy', label: 'Fluroscopy' },
  { value: 'Bone Densitometry (DEXA Scan)', label: 'Bone Densitometry (DEXA Scan)' },
  { value: 'X-ray', label: 'X-ray' },
  { value: 'Minimally Invasive Procedures', label: 'Minimally Invasive Procedures' },
  { value: 'Pain Management', label: 'Pain Management' },
  { value: 'Mammography', label: 'Mammography' },
  { value: 'Breast Ultrasound', label: 'Breast Ultrasound' },
  { value: 'Remote Reporting', label: 'Remote Reporting' },
  { value: 'Advanced Technology', label: 'Advanced Technology' },
  { value: '3D Pregnancy Scan', label: '3D Pregnancy Scan' }
];

const INITIAL_FORM_STATE = {
  patientType: 'existing',
  patientId: '',
  employeeId: '',
  appointmentDate: '',
  appointmentTime: '',
  status: 'pending',
  notes: '',
  services: [],
  // New patient fields
  title: '',
  firstName: '',
  surname: '',
  idOrPassport: '',
  phoneNumber: '',
  alternateNumber: '',
  email: '',
  dob: '',
  gender: '',
  nextOfKin: '',
  postalAddress: '',
  physicalAddress: '',
  paymentStatus: ''
};
 const date = new Date();
  date.setDate(date.getDate() - 1);
// Validation Schema
const validationSchema = Yup.object().shape({
  patientType: Yup.string().required('Patient type is required'),
  patientId: Yup.string().when('patientType', {
    is: 'existing',
    then: () => Yup.string().required('Patient is required'),
    otherwise: () => Yup.string().nullable()
  }),
  employeeId: Yup.string().nullable(),
  appointmentDate: Yup.string()
    .required('Appointment date is required'),
  appointmentTime: Yup.string().required('Appointment time is required'),
  status: Yup.string().required('Status is required'),
  services: Yup.array().min(1, 'At least one service is required'),

  title: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().nullable()
  }),
  firstName: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().required('First name is required')
      .matches(/^[a-zA-Z\s]*$/, 'Only letters are allowed')
  }),
  surname: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().nullable()
      .matches(/^[a-zA-Z\s]*$/, 'Only letters are allowed')
  }),
  idOrPassport: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().nullable()
  }),
  phoneNumber: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().nullable()
  }),
  email: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string()
      .email('Invalid email format').optional().nullable()
  }),
  dob: Yup.string().nullable(),
  gender: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().nullable(),
  }),
  nextOfKin: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().nullable(),
  }),
  postalAddress: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().nullable(),
  }),
  physicalAddress: Yup.string().when('patientType', {
    is: 'new',
    then: () => Yup.string().nullable(),
  }),
  paymentStatus: Yup.string().nullable()
});

export const getChangedValues = (original, current) => {
  if (!original) return current;

  const changes = {};
  
  // Compare primitive fields
  Object.keys(current).forEach(key => {
    if (['services', 'bloodTests', 'referralForms'].includes(key)) return;
    
    if (current[key] !== original[key] && current[key] !== undefined) {
      changes[key] = current[key];
    }
  });

  // Compare services array
  if (current.services?.length || original.services?.length) {
    const servicesChanged = JSON.stringify(current.services) !== JSON.stringify(original.services);
    if (servicesChanged) {
      changes.services = current.services?.map(s => ({
        name: s?.name,
        paymentMethod: s?.paymentMethod,
        typeOfExam: s?.typeOfExam,
        amount: s?.amount
      }));
    }
  }

  // Compare files
  if (current.bloodTests?.length || original.bloodTests?.length) {
    const bloodTestsChanged = JSON.stringify(current.bloodTests) !== JSON.stringify(original.bloodTests);
    if (bloodTestsChanged) {
      changes.bloodTests = current.bloodTests;
    }
  }

  if (current.referralForms?.length || original.referralForms?.length) {
    const referralFormsChanged = JSON.stringify(current.referralForms) !== JSON.stringify(original.referralForms);
    if (referralFormsChanged) {
      changes.referralForms = current.referralForms;
    }
  }

  return Object.keys(changes).length ? changes : null;
};


const AppointmentForm = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [bloodTestFiles, setBloodTestFiles] = useState([]);
  const [referralFiles, setReferralFiles] = useState([]);
  const [errors, setErrors] = useState({}); // State to hold validation errors
  const [patients, setPatients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [service, setService] = useState({ name: "", paymentMethod: "", typeOfExam: "", amount:'' });
  const [openedObj, setOpenedObj] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const appointmentId = queryParams.get('id');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [patientsList, employeesList] = await Promise.all([
          fetchPatientsForDropdown(),
          fetchEmployeesForDropdown()
        ]);
        console.log('patientsList:', patientsList);
        console.log('employeesList:', employeesList);
        setPatients(patientsList);
        setEmployees(employeesList);

        if (appointmentId) {
          const appointmentData = await getAppointmentById(appointmentId);
          console.log('appointmentData:', appointmentData.data);
          const objectWithNotNullValues = Object.fromEntries(
            Object.entries(appointmentData.data).filter(([_, v]) => v)
          );
          const data = {
            ...objectWithNotNullValues,
            appointmentDate: new Date(objectWithNotNullValues.appointmentDate).toISOString().split('T')[0],
            patientType: 'existing',
          }
          setOpenedObj(data);
          setFormData(data);
          setBloodTestFiles(appointmentData?.data?.bloodTests || []);
          setReferralFiles(appointmentData?.data?.referralForms || []);
        }
      } catch (error) {
        alertify.error('Error loading data');
        console.error('Error fetching data:', error.response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appointmentId]);


  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAddService = () => {
    if (!service.name || !service.paymentMethod) return;

    setFormData(prev => ({
      ...prev,
      services: [...prev.services, service]
    }));
    setService({ name: '', paymentMethod: '', amount: '', typeOfExam: '' });
    setErrors(prev => ({ ...prev, services: undefined }));
  };

  const handleRemoveService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      return true;
    } catch (err) {
      console.log(err, "erro")
      const validationErrors = {};
      err?.inner?.forEach((error) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) return;
    console.log('formData:', formData);

    try {
      setIsSubmitting(true);
      if (appointmentId) {
        const changedValues = getChangedValues(openedObj, {
          ...formData,
          bloodTests:bloodTestFiles?.map(b => {
            delete b.id;
            delete b.appointmentId;
            return b;
          }),
          referralForms: referralFiles?.map(r => {
            delete r.id;
            delete r.appointmentId;
            return r;
          }),
          services: formData.services?.map(s => {
            delete s.id;
            delete s.appointmentId;
            return s;
          })
        });
  
        if (!changedValues) {
          alertify.error('No changes detected');
          return;
        }
  
        await updateAppointment(appointmentId, changedValues);
        alertify.success('Appointment updated successfully!');
      } else {
        await createAppointment({
          ...formData,
          bloodTests:bloodTestFiles,
          referralForms: referralFiles
        });
        alertify.success('Appointment created successfully!');
      }
      history.push('/main/appointments');
    } catch (error) {
      alertify.error(error?.response?.data?.message);
      console.error('Error saving appointment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const customStyle = {
    control: (provided, state) => ({
      ...provided,
      background: '#fff',
      minHeight: '40px',
      height: '40px',
      borderRadius: '10px',
    }),

    valueContainer: (provided, state) => ({
      ...provided,
      height: '40px',
      padding: '0 6px'
    }),

    input: (provided, state) => ({
      ...provided,
      margin: '0px',
    }),

    indicatorsContainer: (provided, state) => ({
      ...provided,
      height: '40px',
    }),
  }


  const renderPatientForm = () => (
    <>
      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Title</label>
          <select
            name="title"
            className={`form-control ${errors.title ? 'is-invalid' : ''}`}
            value={formData.title}
            onChange={handleFormChange}
          >
            <option value="">Select Title</option>
            {['Mr', 'Mrs', 'Miss', 'Ms']?.map(title => (
              <option key={title} value={title}>{title}</option>
            ))}
          </select>
          {errors.title && <div className="invalid-feedback">{errors.title}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>First Name</label>
          <input
            type="text"
            className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
            name="firstName"
            placeholder="Enter First Name"
            value={formData.firstName}
            onChange={handleFormChange}
          />
          {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Surname</label>
          <input
            type="text"
            className={`form-control ${errors.surname ? 'is-invalid' : ''}`}
            name="surname"
            placeholder="Enter Surname"
            value={formData.surname}
            onChange={handleFormChange}
          />
          {errors.surname && <div className="invalid-feedback">{errors.surname}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>ID/Passport</label>
          <input
            type="text"
            className={`form-control ${errors.idOrPassport ? 'is-invalid' : ''}`}
            name="idOrPassport"
            placeholder="Enter ID or Passport"
            value={formData.idOrPassport}
            onChange={handleFormChange}
          />
          {errors.idOrPassport && <div className="invalid-feedback">{errors.idOrPassport}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="text"
            className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
            name="phoneNumber"
            placeholder="+267 7xxxxxxx"
            value={formData.phoneNumber}
            onChange={handleFormChange}
          />
          {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Alternate Number</label>
          <input
            type="text"
            className="form-control"
            name="alternateNumber"
            placeholder="Enter Alternate Number"
            value={formData.alternateNumber}
            onChange={handleFormChange}
          />
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
            name="email"
            placeholder="Enter Email"
            value={formData.email}
            onChange={handleFormChange}
          />
          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Date of Birth</label>
          <input
            type="date"
            className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
            name="dob"
            placeholder="Enter Date of Birth"
            value={formData.dob}
            onChange={handleFormChange}
          />
          {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Gender</label>
          <select
            className={`form-control ${errors.gender ? 'is-invalid' : ''}`}
            name="gender"
            value={formData.gender}
            onChange={handleFormChange}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
          {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Next of Kin</label>
          <input
            type="text"
            className={`form-control ${errors.nextOfKin ? 'is-invalid' : ''}`}
            name="nextOfKin"
            placeholder="Enter Next of Kin"
            value={formData.nextOfKin}
            onChange={handleFormChange}
          />
          {errors.nextOfKin && <div className="invalid-feedback">{errors.nextOfKin}</div>}
        </div>
      </div>


      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Postal Address</label>
          <input
            type="text"
            className={`form-control ${errors.postalAddress ? 'is-invalid' : ''}`}
            name="postalAddress"
            placeholder="Enter Postal Address"
            value={formData.postalAddress}
            onChange={handleFormChange}
          />
          {errors.postalAddress && <div className="invalid-feedback">{errors.postalAddress}</div>}
        </div>
      </div>

      <div className="col-lg-3 col-sm-6 col-12">
        <div className="form-group">
          <label>Physical Address</label>
          <input
            type="text"
            className={`form-control ${errors.physicalAddress ? 'is-invalid' : ''}`}
            name="physicalAddress"
            placeholder="Enter Physical Address"
            value={formData.physicalAddress}
            onChange={handleFormChange}
          />
          {errors.physicalAddress && <div className="invalid-feedback">{errors.physicalAddress}</div>}
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="page-title">
              <h4>Appointment Management</h4>
              <h6>{appointmentId ? "Update" : "Add"} Appointment</h6>
            </div>
          </div>
          {/* /Form */}
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div>Loading...</div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                  {
                    !appointmentId &&
                   (<div className="col-lg-12">
                      <div className="form-group mb-1">
                        <label className="mb-2">Patient Type</label>
                        <div className="d-flex gap-4">
                          <Form.Check
                            type="radio"
                            label="Select Existing Patient"
                            name="patientType"
                            checked={formData.patientType === 'existing'}
                            onChange={
                              () => handleFormChange({ target: { name: 'patientType', value: 'existing' } })
                            }
                          />
                          <Form.Check
                            type="radio"
                            label="Add New Patient"
                            name="patientType"
                            checked={formData.patientType === 'new'}
                            onChange={
                              () => handleFormChange({ target: { name: 'patientType', value: 'new' } })
                            }
                          />
                        </div>
                      </div>
                    </div>)
                    }
                    {formData.patientType === 'new'  && !appointmentId && renderPatientForm()}
                    {/* Patient Field */}
                    {
                      formData.patientType === 'existing' && (
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="form-group" style={{ height: 20 }}>
                            <label>Patient Name</label>
                            <Select
                              options={patients}
                              value={patients.find((p) => p.value === formData.patientId)}
                              onChange={(selected) => {
                                setFormData({ ...formData, patientId: selected.value });
                                setErrors({ ...errors, patientId: undefined });
                             }}
                              isSearchable
                              styles={customStyle}
                              placeholder="Select Patient"
                            />
                            {errors.patientId && <div className="text-danger">{errors.patientId}</div>}
                          </div>
                        </div>
                      )
                    }

                    {/* Employee Field */}
                    <div className="col-lg-3 col-sm-6 col-12">
                      <div className="form-group">
                        <label>Consulting Doctor</label>
                        <Select
                          className="select"
                          options={employees}
                          value={employees.find((e) => e.value === formData.employeeId)}
                          onChange={(selected) => {
                            setFormData({ ...formData, employeeId: selected.value });
                            setErrors({ ...errors, employeeId: undefined });
                          }}
                          isSearchable
                          styles={customStyle}
                          placeholder="Select Doctor"
                        />
                        {errors.employeeId && <div className="text-danger">{errors.employeeId}</div>}
                      </div>
                    </div>

                   


                    {/* Appointment Date */}
                    <div className="col-lg-3 col-sm-6 col-12">
                      <div className="form-group">
                        <label>Appointment Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="appointmentDate"
                          value={formData.appointmentDate}
                          onChange={handleFormChange}
                        />
                        {errors.appointmentDate && (
                          <div className="text-danger">{errors.appointmentDate}</div>
                        )}
                      </div>
                    </div>

                    {/* Appointment Time */}
                    <div className="col-lg-3 col-sm-6 col-12">
                      <div className="form-group">
                        <label>Appointment Time</label>
                        <input
                          type="time"
                          className="form-control"
                          name="appointmentTime"
                          value={formData.appointmentTime}
                          onChange={handleFormChange}
                        />
                        {errors.appointmentTime && (
                          <div className="text-danger">{errors.appointmentTime}</div>
                        )}
                      </div>
                    </div>

                     {/* Status */}
                     <div className="col-lg-3 col-sm-6 col-12">
                      <div className="form-group">
                        <label>Appointment Status</label>
                        <select
                          className="form-control"
                          name="status"
                          value={formData.status}
                          onChange={handleFormChange}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="closed">Closed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        {errors.status && (
                          <div className="text-danger">{errors.status}</div>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-lg-3 col-sm-6 col-12">
                      <div className="form-group">
                        <label>Appointment Status</label>
                        <select
                          className="form-control"
                          name="paymentStatus"
                          value={formData.paymentStatus}
                          onChange={handleFormChange}
                        >
                          <option value="unpaid">Unpaid</option>
                          <option value="paid">Paid</option>
                          <option value="partially_paid">Partially Paid</option>
                        </select>
                        {errors.status && (
                          <div className="text-danger">{errors.paymentStatus}</div>
                        )}
                      </div>
                    </div>


                    {/* Notes */}
                    <div className="col-lg-12">
                      <div className="form-group">
                        <label>Notes</label>
                        <textarea
                          className="form-control"
                          name="notes"
                          value={formData.notes}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>


                    <div>
                      <FileUpload onFilesChange={(newFiles) => {
                        setBloodTestFiles(newFiles);
                      }}
                      defaultFiles={bloodTestFiles}
                      title="Lab Results"
                       />
                    </div>

                    <div>
                      <FileUpload onFilesChange={(newFiles) => {
                        setReferralFiles(newFiles);
                      }}
                      defaultFiles={referralFiles}
                      title="Referral Forms"
                       />
                    </div>
                    

                    {/* Services Section */}
                    <div className="col-12">
                      <h5 className="mb-3">Services</h5>
                      <div className="row align-items-center">
                        <div className="col-lg-3 col-sm-6 col-12">
                          <div className="form-group">
                            <Select
                              options={SERVICES}
                              value={SERVICES.find(s => s.value === service.name) || null}
                              onChange={(selected) => setService(prev => ({
                                ...prev,
                                name: selected.value
                              }))}
                              styles={customStyle}

                              placeholder="Select Service"
                            />
                          </div>
                        </div>
                        <div className="col-lg-2 col-sm-6 col-12">
                          <div className="form-group">
                            <input
                              type="text"
                              placeholder="Type Of Exam"
                              className="form-control"
                              value={service.typeOfExam}
                              onChange={(e) =>
                                setService({ ...service, typeOfExam: e.target.value })
                              }
                            />
                          </div>
                        </div>
                        <div className="col-lg-2 col-sm-6 col-12">
                          <div className="form-group">
                            <Select
                              options={PAYMENT_METHODS}
                              value={PAYMENT_METHODS.find(p => p.value === service.paymentMethod)  || null}
                              onChange={(selected) => setService(prev => ({
                                ...prev,
                                paymentMethod: selected.value
                              }))}
                              styles={customStyle}
                              placeholder="Payment Method"
                            />
                          </div>
                        </div>
                      

                        <div className="col-lg-2 col-sm-6 col-12">
                          <div className="form-group">
                            <input
                              type="number"
                              placeholder="Amount"
                              className="form-control"
                              value={service.amount}
                              onChange={(e) =>
                                setService({ ...service, amount: e.target.value })
                              }
                            />
                          </div>
                        </div>

                        <div className="col-lg-3 text-end mb-4 d-flex align-items-center gap-2">
                          <button
                            type="button"
                            style={{
                              fontSize: "15px",
                              fontFamily: "sans-serif",
                              fontWeight: "500",
                              borderRadius: "10px",
                            }}
                            onClick={() =>
                              setService({ name: "", paymentMethod: "", typeOfExam: "", amount:'' })
                            }
                            className="btn btn-danger py-2 px-4"
                          >
                            Clear
                          </button>

                          <button
                            type="button"
                            style={{
                              fontSize: "15px",
                              fontFamily: "sans-serif",
                              fontWeight: "500",
                              borderRadius: "10px",
                            }}
                            className="btn btn-primary py-2 px-4"
                            onClick={handleAddService}
                            disabled={!service.name || !service.paymentMethod || !service.amount}
                          >
                            Add
                          </button>
                        </div>

                        <div className="col-12 mb-4">
                          <div className="responsive-table">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Service</th>
                                <th>Type Of Exam.</th>
                                <th>Payment Method</th>
                                <th>Amount</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formData.services?.map((s, index) => (
                                <tr key={index}>
                                  <td>{s.name}</td>
                                  <td>{s.typeOfExam}</td>
                                  <td>{s.paymentMethod}</td>
                                  <td>
                                  P {s.amount}
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn text-danger "
                                      onClick={() => handleRemoveService(index)}
                                    >
                                      <X size={20} className="me-2" />
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                          {errors.services && (
                            <div className="text-danger my-4 text-center">{errors.services}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Submit and Cancel Buttons */}
                    <div className="col-lg-12 d-flex justify-content-end">
                      <button className="btn btn-submit me-2 d-flex align-items-center justify-content-center w-fit" style={{ width: 200 }} type="submit" disabled={isSubmitting}>
                        {
                          isSubmitting && <span className="spinner-border spinner-border-sm ms-2 me-2" role="status" aria-hidden="true"></span>
                        }
                        {appointmentId ? "Update Appointment" : "Add Appointment"}
                      </button>
                      <button
                        className="btn btn-cancel"
                        type="button"
                        onClick={() => history.push("/appointments")}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
          {/* /Form */}
        </div>
      </div>
    </>
  );
};

export default AppointmentForm;